import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, FilePlus2, Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import CompanyCard from '@/components/app/CompanyCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompaniesListPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const companiesRes = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(companiesRes.data.companies || []);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load companies';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Companies grid (backend returns enriched RA/MF status fields)

  return (
    <AppLayout>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">My Companies</h1>
          <p className="text-gray-400 text-sm mt-1">Manage your business entities</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/dashboard/companies/self-register')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium border border-gray-700 text-gray-200 hover:border-[#059669] hover:text-[#059669] transition-colors"
            data-testid="add-existing-company-btn"
          >
            <FilePlus2 className="w-5 h-5" />
            <span className="hidden sm:inline">Add Existing Company</span>
            <span className="sm:hidden">Existing</span>
          </button>
          <button
            onClick={() => navigate('/dashboard/onboarding/international')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: '#059669' }}
            data-testid="add-company-btn"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Add New Company</span>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Loading companies...</div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
          <p className="text-red-400">{error}</p>
          <button 
            onClick={fetchData}
            className="mt-2 text-sm text-red-400 hover:text-red-300 underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && companies.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">No companies yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Add your first company to get started with formation, compliance, and more.
          </p>
          <button
            onClick={() => navigate('/dashboard/companies/new')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: '#059669' }}
            data-testid="add-first-company-btn"
          >
            <Plus className="w-5 h-5" />
            Add Your First Company
          </button>
        </div>
      )}

      {/* Companies Grid */}
      {!loading && !error && companies.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="companies-grid">
          {companies.map(company => (
            <CompanyCard 
              key={company.id} 
              company={company} 
            />
          ))}
        </div>
      )}
    </AppLayout>
  );
};

export default CompaniesListPage;
